import { CardBody, Icon, Id, Loading } from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { IApplicationCarFleetResource } from "../../../shared/types/application";
import {
	List,
	ListItem,
	ListItemLink,
	ListItemSubtitle,
	ListItemTrailing,
} from "../../../shared/ui/list/listComponents";
import { ListItemTitle } from "../../../shared/ui/list/ListItemTitle";
import { DateTimeDurationDistance } from "../common/DurationDistance";
import { DurationSubtitle } from "../common/DurationSubtitle";
import { LoadingOverlay } from "../common/LoadingOverlay";
import { ViewAllListItemTitle } from "../common/ViewAllListItemTitle";
import { currentAndUpcomingTestDrivesWidgetService } from "./service";

const StyledCardBody = styled(CardBody)`
	padding: 0;
`;

const StyledListItemLink = styled(ListItemLink)`
	display: grid;
	grid-template-columns: repeat(6, 1fr);
	align-items: center;
`;

const filter = window.encodeURIComponent(
	btoa(
		JSON.stringify([
			{
				valueKey: "end",
				operator: ">",
				value: format(Date.now(), "yyyy-MM-dd"),
			},
		]),
	),
);

export const TestDrivesList: FC<{ application: IApplicationCarFleetResource; tenantId: Id }> = ({
	application,
	tenantId,
}) => {
	const { t } = useTranslation();
	const { data, isLoading } = useQuery({
		queryKey: currentAndUpcomingTestDrivesWidgetService.getKey(application.id, tenantId),
		queryFn: () => currentAndUpcomingTestDrivesWidgetService.get(application.id, tenantId).then((x) => x.data.data),
	});
	const testDrives = data?.testDrives ?? [];
	const total = data?.total ?? 0;
	const hasTestDrives = !!data?.testDrives.length;
	const moreCount = total - (data?.testDrives.length ?? 0);

	return (
		<>
			<StyledCardBody>
				<List>
					{testDrives.map((x) => (
						<ListItem key={x.id}>
							<StyledListItemLink to={`${application.adminUrl}/test-drives/${x.id}`}>
								<ListItemTitle>
									{t(x.type)}
									<DurationSubtitle duration={x} />
								</ListItemTitle>
								<ListItemTitle>
									{`${x.contact?.firstName} ${x.contact?.lastName}`}
									<ListItemSubtitle>{x.company?.name}</ListItemSubtitle>
								</ListItemTitle>
								<ListItemTitle>{t(x.status)}</ListItemTitle>
								<ListItemTitle>
									{t("License Plate")}
									<ListItemSubtitle>{x.licensePlate}</ListItemSubtitle>
								</ListItemTitle>
								<ListItemTitle>
									{t("Series")}
									<ListItemSubtitle>{x.model}</ListItemSubtitle>
								</ListItemTitle>
								<ListItemTrailing>
									<DateTimeDurationDistance duration={x} />
								</ListItemTrailing>
							</StyledListItemLink>
						</ListItem>
					))}
					{!total && (
						<ListItem>
							<ViewAllListItemTitle>{t("No data available.")}</ViewAllListItemTitle>
						</ListItem>
					)}
					{hasTestDrives && (
						<ListItem>
							<ListItemLink
								to={`${application.apiUrl}/redirect/change-tenant/${tenantId}?redirectTo=/test-drives/all?testDrivesAllFilter=${filter}`}
							>
								<ViewAllListItemTitle>
									<Icon $icon="external_link" style={{ width: 16, height: 16 }} />
									{t("View all")}
									{!!moreCount && <ListItemSubtitle>({t("{{moreCount}} more", { moreCount })})</ListItemSubtitle>}
								</ViewAllListItemTitle>
							</ListItemLink>
						</ListItem>
					)}
				</List>
			</StyledCardBody>
			{isLoading && (
				<LoadingOverlay>
					<Loading />
				</LoadingOverlay>
			)}
		</>
	);
};
